import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/stores/authStore";
import { API_BASE_URL } from "@/utils/apiBaseUrl";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

export default function Profile() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const token = useAuthStore((s) => s.token);
  const userId = user?.id;
  const fullNameUtilisateur = user?.fullName ?? "";

  const [fullName, setFullName] = useState(user?.fullName || "");
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");

  const titre = useMemo(() => {
    if (!user) return "Profil";
    return user.email ? `Profil (${user.email})` : "Profil";
  }, [user]);

  const fullNameAChange = useMemo(() => {
    const initial = user?.fullName || "";
    return fullName !== initial;
  }, [fullName, user?.fullName]);

  useEffect(() => {
    if (!token) return;
    if (!userId) return;

    let cancelled = false;

    const chargerProfil = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/me`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          return;
        }

        const payload = (await response.json()) as {
          user?: { fullName?: string | null };
        };

        if (!cancelled && payload.user) {
          const nextFullName = payload.user.fullName ?? "";
          setFullName(nextFullName);
          if (nextFullName !== fullNameUtilisateur) {
            const userActuel = useAuthStore.getState().user;
            if (userActuel?.id === userId) {
              setUser({
                ...userActuel,
                fullName: payload.user.fullName ?? undefined,
              });
            }
          }
        }
      } catch {
        // Pas bloquant pour l'écran profil
      }
    };

    void chargerProfil();

    return () => {
      cancelled = true;
    };
  }, [token, userId, fullNameUtilisateur, setUser]);

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    setInfo("");
    setError("");

    if (!user || !token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fullName }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const message =
          typeof payload?.message === "string"
            ? payload.message
            : "Impossible de mettre à jour le profil.";
        throw new Error(message);
      }

      const payload = (await response.json()) as {
        user?: { fullName?: string | null };
      };

      const next = {
        ...user,
        fullName:
          payload.user?.fullName ?? (fullName.trim() || undefined),
      };
      setUser(next);
      setInfo("Profil mis à jour");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Erreur lors de la mise à jour.";
      setError(message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Button
        asChild
        variant="secondary"
        size="default"
        className="fixed left-4 top-4 z-50 shadow-md border border-slate-200/70 dark:border-slate-700/60 bg-white/95 dark:bg-slate-900/90 backdrop-blur"
      >
        <Link to="/" aria-label="Retour a l'accueil" className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          <span>Retour</span>
        </Link>
      </Button>

      <main className="pt-16 px-6 pb-10">
        <div className="max-w-xl mx-auto">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {titre}
              </h1>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                Consultez les informations de votre compte.
              </p>
            </div>
          </div>

          {!user ? (
            <Alert className="mt-6">
              <AlertDescription>
                Impossible de charger le profil utilisateur.
              </AlertDescription>
            </Alert>
          ) : (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Informations</CardTitle>
              </CardHeader>
              <CardContent>
                {info ? (
                  <Alert className="mb-4">
                    <AlertDescription>{info}</AlertDescription>
                  </Alert>
                ) : null}
                {error ? (
                  <Alert className="mb-4">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ) : null}

                <form onSubmit={enregistrer} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="profil-nom">Nom complet</Label>
                    <Input
                      id="profil-nom"
                      value={fullName}
                      placeholder="Non renseigné"
                      onChange={(e) => setFullName(e.target.value)}
                      autoComplete="name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="profil-email">Email</Label>
                    <Input
                      id="profil-email"
                      value={user.email || ""}
                      readOnly
                      aria-readonly="true"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      L&apos;email n&apos;est pas modifiable.
                    </p>
                  </div>

                  {user.teamId ? (
                    <div className="space-y-2">
                      <Label htmlFor="profil-team">Équipe</Label>
                      <Input
                        id="profil-team"
                        value={user.teamId}
                        readOnly
                        aria-readonly="true"
                      />
                    </div>
                  ) : null}

                  <CardFooter className="px-0 pb-0 pt-2 gap-2">
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={!fullNameAChange}
                    >
                      Enregistrer
                    </Button>
                  </CardFooter>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
